import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { useTranslation } from 'react-i18next';
import { isRtlLanguage } from './i18n';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { TeacherAuthProvider, useTeacherAuth } from './hooks/useTeacherAuth';
import { ParentAuthProvider, useParentAuth } from './hooks/useParentAuth';
import LoadingSpinner from './components/LoadingSpinner';
import AppBackground from './components/AppBackground';
import './utils/token-helper';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AccountSettings from './pages/AccountSettings';
import Dashboard from './pages/Dashboard';
import Vacations from './pages/Vacations';
import Holidays from './pages/Holidays';
import Cameras from './pages/Cameras';
import Children from './pages/Children';
import Classrooms from './pages/Classrooms';
import Schedules from './pages/Schedules';
import EdgeDevices from './pages/EdgeDevices';
import DailyAttendance from './pages/DailyAttendance';
import ChildTimeline from './pages/ChildTimeline';
import Reports from './pages/Reports';
import CustomReports from './pages/CustomReports';
import Policies from './pages/Policies';
import Settings from './pages/Settings';
import RoleManagement from './pages/RoleManagement';
import UserManagement from './pages/UserManagement';
import UserNurseryAssignments from './pages/UserNurseryAssignments';
import NurseryManagement from './pages/NurseryManagement';
import HierarchyManagement from './pages/HierarchyManagement';
import Notifications from './pages/Notifications';
import SnapshotSettings from './pages/SnapshotSettings';
import ChildImageDisplaySettings from './pages/ChildImageDisplaySettings';
import CameraSnapshots from './pages/CameraSnapshots';
import AnalysisPrompts from './pages/AnalysisPrompts';
import TeacherLogin from './pages/TeacherLogin';
import TeacherChangePassword from './pages/TeacherChangePassword';
import TeacherDashboard from './pages/TeacherDashboard';
import TeacherEvents from './pages/TeacherEvents';
import AdminEvents from './pages/AdminEvents';
import ParentLogin from './pages/ParentLogin';
import ParentDashboard from './pages/ParentDashboard';
import ParentSnapshots from './pages/ParentSnapshots';
import ParentSubscribe from './pages/ParentSubscribe';
import ParentSettings from './pages/ParentSettings';
import ParentEvents from './pages/ParentEvents';
import Teachers from './pages/Teachers';
import Parents from './pages/Parents';
import Subscriptions from './pages/Subscriptions';
import NurseryPayouts from './pages/NurseryPayouts';
import ConsentForms from './pages/ConsentForms';

const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
  key: 'muiltr',
});

const nunitoFamily = '"Nunito", "Segoe UI", "Helvetica Neue", Arial, sans-serif';

const softShadows = [
  'none',
  '0 1px 3px rgba(15,23,42,0.04)',
  '0 2px 6px rgba(15,23,42,0.05)',
  '0 3px 10px rgba(15,23,42,0.06)',
  '0 4px 14px rgba(15,23,42,0.06)',
  '0 5px 18px rgba(15,23,42,0.07)',
  '0 6px 22px rgba(15,23,42,0.07)',
  '0 7px 24px rgba(15,23,42,0.08)',
  '0 8px 28px rgba(15,23,42,0.08)',
  '0 9px 30px rgba(15,23,42,0.09)',
  '0 10px 32px rgba(15,23,42,0.09)',
  '0 11px 34px rgba(15,23,42,0.10)',
  '0 12px 36px rgba(15,23,42,0.10)',
  '0 13px 38px rgba(15,23,42,0.11)',
  '0 14px 40px rgba(15,23,42,0.11)',
  '0 15px 42px rgba(15,23,42,0.12)',
  '0 16px 44px rgba(15,23,42,0.12)',
  '0 17px 46px rgba(15,23,42,0.13)',
  '0 18px 48px rgba(15,23,42,0.13)',
  '0 19px 50px rgba(15,23,42,0.14)',
  '0 20px 52px rgba(15,23,42,0.14)',
  '0 21px 54px rgba(15,23,42,0.15)',
  '0 22px 56px rgba(15,23,42,0.15)',
  '0 23px 58px rgba(15,23,42,0.16)',
  '0 24px 60px rgba(15,23,42,0.16)',
] as unknown as import('@mui/material/styles').Shadows;

function createAppTheme(direction: 'ltr' | 'rtl') {
  return createTheme({
    direction,
    palette: {
      primary: {
        main: '#49B9D9',
        light: '#7ED5EA',
        dark: '#269BB2',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#269BB2',
        light: '#5EC4E0',
        dark: '#1E8499',
        contrastText: '#ffffff',
      },
      success: {
        main: '#16a34a',
        light: '#bbf7d0',
        dark: '#15803d',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#f59e0b',
        light: '#fef3c7',
        dark: '#d97706',
        contrastText: '#ffffff',
      },
      error: {
        main: '#ef4444',
        light: '#fee2e2',
        dark: '#dc2626',
        contrastText: '#ffffff',
      },
      info: {
        main: '#0891b2',
        light: '#cffafe',
        dark: '#0e7490',
        contrastText: '#ffffff',
      },
      grey: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
      },
      background: {
        default: 'transparent',
        paper: '#ffffff',
      },
      text: {
        primary: '#1e293b',
        secondary: '#64748b',
      },
      divider: 'rgba(15,23,42,0.08)',
    },
    typography: {
      fontFamily: nunitoFamily,
      h1: { fontWeight: 800, fontSize: '2.25rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.25, letterSpacing: '-0.01em' },
      h3: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.3 },
      h4: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.35 },
      h5: { fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.4 },
      h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.4 },
      subtitle1: { fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.5 },
      subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.5 },
      body1: { fontWeight: 400, fontSize: '0.9375rem', lineHeight: 1.6 },
      body2: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.6 },
      caption: { fontWeight: 400, fontSize: '0.8125rem', lineHeight: 1.5, color: '#64748b' },
      button: { fontWeight: 600, fontSize: '0.875rem', letterSpacing: '0.01em' },
      overline: { fontWeight: 700, fontSize: '0.6875rem', letterSpacing: '0.08em', textTransform: 'uppercase' as const },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: softShadows,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: 'transparent',
            color: '#1e293b',
          },
          '*': {
            boxSizing: 'border-box',
          },
          '::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '3px',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: '#94a3b8',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 10,
            padding: '8px 20px',
            transition: 'all 180ms ease',
          },
          sizeLarge: {
            padding: '12px 28px',
            fontSize: '0.95rem',
            borderRadius: 12,
          },
          sizeSmall: {
            padding: '5px 14px',
            fontSize: '0.8125rem',
            borderRadius: 8,
          },
          contained: ({ theme }: any) => ({
            boxShadow: '0 2px 8px rgba(102,126,234,0.25)',
            '&:hover': {
              boxShadow: '0 4px 14px rgba(102,126,234,0.35)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.grey[200],
              color: theme.palette.grey[400],
              boxShadow: 'none',
            },
          }),
          outlined: () => ({
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor: 'rgba(102,126,234,0.04)',
            },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: 'all 150ms ease',
            '&:hover': {
              backgroundColor: 'rgba(102,126,234,0.08)',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'medium',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: '#ffffff',
              transition: 'all 150ms ease',
              '& fieldset': {
                borderColor: '#e2e8f0',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: '#94a3b8',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            color: '#64748b',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            marginTop: 6,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 1px 4px rgba(15,23,42,0.04)',
            transition: 'box-shadow 200ms ease, transform 200ms ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(15,23,42,0.08)',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 16,
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          backdrop: {
            backgroundColor: 'rgba(15,23,42,0.45)',
            backdropFilter: 'blur(4px)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            boxShadow: '0 24px 48px rgba(15,23,42,0.16)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            fontSize: '1.2rem',
            padding: '20px 24px 12px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '12px 24px 20px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 24px 20px',
            gap: 8,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            border: 'none',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backdropFilter: 'blur(12px)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }: any) => ({
            borderRadius: 10,
            marginBottom: theme.spacing(0.5),
            padding: '8px 12px',
            transition: 'all 150ms ease',
            '&:hover': {
              backgroundColor: 'rgba(73,185,217,0.08)',
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              '& .MuiListItemIcon-root': {
                color: theme.palette.common.white,
              },
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
            color: '#64748b',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: 8,
            height: 28,
          },
          filled: {
            border: 'none',
          },
          outlined: {
            borderWidth: '1.5px',
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: 700,
            fontSize: '0.7rem',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            fontWeight: 500,
            alignItems: 'center',
          },
          standardError: {
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            border: '1px solid rgba(239,68,68,0.2)',
          },
          standardSuccess: {
            backgroundColor: '#dcfce7',
            color: '#166534',
            border: '1px solid rgba(22,163,74,0.2)',
          },
          standardWarning: {
            backgroundColor: '#fef3c7',
            color: '#92400e',
            border: '1px solid rgba(245,158,11,0.2)',
          },
          standardInfo: {
            backgroundColor: '#e0f2fe',
            color: '#075985',
            border: '1px solid rgba(8,145,178,0.2)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#1e293b',
            borderRadius: 8,
            fontSize: '0.8125rem',
            fontWeight: 500,
            padding: '6px 12px',
          },
          arrow: {
            color: '#1e293b',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 700,
              fontSize: '0.8125rem',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.05em',
              color: '#64748b',
              backgroundColor: '#f8fafc',
              borderBottom: '2px solid #e2e8f0',
              padding: '12px 16px',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid #f1f5f9',
            padding: '12px 16px',
            fontSize: '0.875rem',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 150ms ease',
            '&:hover': {
              backgroundColor: '#f8fafc',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            borderRadius: '3px 3px 0 0',
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9rem',
            minHeight: 48,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 8px 30px rgba(15,23,42,0.12)',
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 6px',
            padding: '8px 12px',
            fontSize: '0.875rem',
            transition: 'all 150ms ease',
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            padding: 7,
          },
          thumb: {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
          track: {
            borderRadius: 11,
            opacity: 0.3,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              borderRadius: 8,
              fontWeight: 600,
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: '#f1f5f9',
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            strokeLinecap: 'round' as const,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(15,23,42,0.08)',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
        },
      },
    },
  });
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useTranslation('common');

  if (loading) {
    return <LoadingSpinner message={t('loading.authenticating')} />;
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route Component - redirects logged-in users to home
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useTranslation('common');

  if (loading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  return !user ? <>{children}</> : <Navigate to="/" replace />;
};

const TeacherProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { teacher, loading } = useTeacherAuth();
  const { t } = useTranslation('common');
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner message={t('loading.authenticating')} />;
  }

  if (!teacher) {
    return <Navigate to="/teacher/login" replace />;
  }

  if (teacher.must_change_password && location.pathname !== '/teacher/change-password') {
    return <Navigate to="/teacher/change-password" replace />;
  }

  return <>{children}</>;
};

const TeacherPublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { teacher, loading } = useTeacherAuth();
  const { t } = useTranslation('common');

  if (loading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  if (!teacher) {
    return <>{children}</>;
  }

  return <Navigate to={teacher.must_change_password ? '/teacher/change-password' : '/teacher/dashboard'} replace />;
};

const ParentProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { parent, loading } = useParentAuth();
  const { t } = useTranslation('common');
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner message={t('loading.authenticating')} />;
  }

  if (!parent) {
    return <Navigate to="/parent/login" replace />;
  }

  if (parent.must_change_password && location.pathname !== '/parent/settings') {
    return <Navigate to="/parent/settings" replace />;
  }

  return <>{children}</>;
};

const ParentPublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { parent, loading } = useParentAuth();
  const { t } = useTranslation('common');

  if (loading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  if (!parent) {
    return <>{children}</>;
  }

  return <Navigate to={parent.must_change_password ? '/parent/settings' : '/parent/dashboard'} replace />;
};

// Nursery Route Component - handles nursery-specific routing
const NurseryRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { nursery_id } = useParams<{ nursery_id: string }>();
  const { nurseries, currentNursery, setCurrentNursery, nurseriesLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!nurseriesLoading && nurseries.length > 0 && nursery_id) {
      // Find the nursery in the available nurseries
      const targetNursery = nurseries.find(c => c.nursery_id === nursery_id);
      
      if (targetNursery) {
        // Set the nursery if it's different from current
        if (!currentNursery || currentNursery.nursery_id !== nursery_id) {
          setCurrentNursery(targetNursery);
        }
      } else {
        // Nursery ID not found, redirect to first available nursery
        const firstNursery = nurseries[0];
        const newPath = location.pathname.replace(`/nursery/${nursery_id}`, `/nursery/${firstNursery.nursery_id}`);
        navigate(newPath, { replace: true });
        return;
      }
    }
  }, [nursery_id, nurseries, currentNursery, setCurrentNursery, nurseriesLoading, navigate, location.pathname]);

  const { t } = useTranslation('common');

  if (nurseriesLoading) {
    return <LoadingSpinner message={t('loading.loadingNurseries')} />;
  }

  if (nurseries.length === 0) {
    return <>{children}</>;
  }

  return <>{children}</>;
};

// Root redirect component - redirects to first nursery's dashboard or no-nursery dashboard
const RootRedirect: React.FC = () => {
  const { nurseries, nurseriesLoading, currentNursery } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!nurseriesLoading) {
      if (nurseries.length > 0) {
        // User has nurseries - redirect to nursery dashboard
        const targetNursery = currentNursery || nurseries[0];
        navigate(`/nursery/${targetNursery.nursery_id}/dashboard`, { replace: true });
      } else {
        // User has no nurseries - redirect to no-nursery dashboard
        navigate('/no-nursery/dashboard', { replace: true });
      }
    }
  }, [nurseries, nurseriesLoading, currentNursery, navigate]);

  const { t } = useTranslation('common');

  if (nurseriesLoading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  return <LoadingSpinner message={t('loading.redirecting')} />;
};

const HierarchyRedirect: React.FC = () => {
  const { nursery_id } = useParams<{ nursery_id?: string }>();
  const target = nursery_id ? `/nursery/${nursery_id}/settings/hierarchy` : '/settings/hierarchy';
  return <Navigate to={target} replace />;
};

// Component to handle intended path redirects
const PathHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if there's an intended path stored from error.html redirect
    const intendedPath = sessionStorage.getItem('intendedPath');
    
    if (intendedPath && location.pathname === '/') {
      // Clear the stored path
      sessionStorage.removeItem('intendedPath');
      
      // Navigate to the intended path
      navigate(intendedPath, { replace: true });
    }
  }, [navigate, location]);

  return null;
};

const AdminRoutes: React.FC = () => {
  return (
    <TeacherAuthProvider>
      <ParentAuthProvider>
      <Routes>
        <Route path="/teacher/login" element={<TeacherPublicRoute><TeacherLogin /></TeacherPublicRoute>} />
        <Route path="/teacher/change-password" element={<TeacherProtectedRoute><TeacherChangePassword /></TeacherProtectedRoute>} />
        <Route path="/teacher/dashboard" element={<TeacherProtectedRoute><TeacherDashboard /></TeacherProtectedRoute>} />
        <Route path="/teacher/events" element={<TeacherProtectedRoute><TeacherEvents /></TeacherProtectedRoute>} />
        <Route path="/parent/login" element={<ParentPublicRoute><ParentLogin /></ParentPublicRoute>} />
        <Route path="/parent/change-password" element={<ParentProtectedRoute><Navigate to="/parent/settings" replace /></ParentProtectedRoute>} />
        <Route path="/parent/dashboard" element={<ParentProtectedRoute><ParentDashboard /></ParentProtectedRoute>} />
        <Route path="/parent/snapshots" element={<ParentProtectedRoute><ParentSnapshots /></ParentProtectedRoute>} />
        <Route path="/parent/subscribe" element={<ParentProtectedRoute><ParentSubscribe /></ParentProtectedRoute>} />
        <Route path="/parent/settings" element={<ParentProtectedRoute><ParentSettings /></ParentProtectedRoute>} />
        <Route path="/parent/events" element={<ParentProtectedRoute><ParentEvents /></ParentProtectedRoute>} />

        {/* Guest Routes - only accessible when not logged in */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* No Nursery Routes - for users not assigned to any nursery */}
        <Route
          path="/no-nursery/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Account Settings Route */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />

        {/* Super Admin Routes - accessible without nursery assignment */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/hierarchy"
          element={
            <ProtectedRoute>
              <HierarchyManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/roles"
          element={
            <ProtectedRoute>
              <RoleManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/users"
          element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/assignments"
          element={
            <ProtectedRoute>
              <UserNurseryAssignments />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/nurseries"
          element={
            <ProtectedRoute>
              <NurseryManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/groups"
          element={<HierarchyRedirect />}
        />

        <Route
          path="/settings/locations"
          element={<HierarchyRedirect />}
        />

        {/* Nursery-specific Protected Routes */}
        <Route
          path="/nursery/:nursery_id/dashboard"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Dashboard />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/vacations"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Vacations />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/events"
          element={<ProtectedRoute><NurseryRoute><AdminEvents /></NurseryRoute></ProtectedRoute>}
        />
        <Route
          path="/nursery/:nursery_id/holidays"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Holidays />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/cameras"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Cameras />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/children"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Children />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/teachers"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Teachers />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/parents"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Parents />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/consent-forms"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <ConsentForms />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/subscriptions"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Subscriptions />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/payouts"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <NurseryPayouts />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/classrooms"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Classrooms />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/schedules"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Schedules />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/edge-devices"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <EdgeDevices />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/daily-attendance"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <DailyAttendance />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/child-timeline"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <ChildTimeline />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/reports"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Reports />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/custom-reports"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <CustomReports />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/snapshots"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <CameraSnapshots />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/settings/snapshot-settings"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <SnapshotSettings />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/settings/child-image-display-settings"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <ChildImageDisplaySettings />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/settings/analysis-prompts"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <AnalysisPrompts />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/policies"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Policies />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nursery/:nursery_id/notifications"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Notifications />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/settings"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <Settings />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/settings/hierarchy"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <HierarchyManagement />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/settings/roles"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <RoleManagement />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/settings/users"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <UserManagement />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/settings/assignments"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <UserNurseryAssignments />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/settings/nurseries"
          element={
            <ProtectedRoute>
              <NurseryRoute>
                <NurseryManagement />
              </NurseryRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/nursery/:nursery_id/settings/groups"
          element={<HierarchyRedirect />}
        />

        <Route
          path="/nursery/:nursery_id/settings/locations"
          element={<HierarchyRedirect />}
        />

        {/* Legacy route redirects - for backward compatibility */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cameras"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/children"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teachers"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parents"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/consent-forms"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payouts"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/classrooms"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedules"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edge-devices"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />

        {/* Remove legacy non-nursery timeline route */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        {/* Default redirect */}
        <Route
          path="/" 
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
      </Routes>
      </ParentAuthProvider>
    </TeacherAuthProvider>
  );
};

function App() {
  const { i18n } = useTranslation();
  const isRtl = isRtlLanguage(i18n.language);
  const theme = useMemo(() => createAppTheme(isRtl ? 'rtl' : 'ltr'), [isRtl]);
  const cache = isRtl ? rtlCache : ltrCache;

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppBackground />
        <AuthProvider>
          <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
          <Router>
            <PathHandler />
            <Routes>
              <Route path="*" element={<AdminRoutes />} />
            </Routes>
          </Router>
          </Box>
        </AuthProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
